/**
 * 可搜索下拉选择器（组合框）
 * 触发框 + 搜索输入 + 选项列表：素材数量多时可按名称/编码关键字过滤
 * 点击外部 / Esc 关闭；选中项高亮打勾
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Check, Search, X } from 'lucide-react';
import styles from './SearchableSelect.module.css';

export interface SearchableOption {
  value: number;
  label: string;
}

interface Props {
  value: number | null;
  options: SearchableOption[];
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: number) => void;
  onClear?: () => void; // 提供 则已选中时显示清除按钮（选择可留空）
}

export default function SearchableSelect({ value, options, placeholder = '请选择', disabled, onChange, onClear }: Props) {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find(o => o.value === value) || null;

  // 打开时清空关键字并聚焦搜索框
  useEffect(() => {
    if (open) {
      setKeyword('');
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // 点击面板外部关闭
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // 按关键字过滤（名称/编码包含即命中）
  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return options;
    return options.filter(o => o.label.toLowerCase().includes(kw));
  }, [options, keyword]);

  return (
    <div className={styles.wrap} ref={boxRef}>
      <button
        type="button"
        className={`${styles.trigger} ${open ? styles.triggerOpen : ''}`}
        disabled={disabled}
        onClick={() => setOpen(v => !v)}
      >
        <span className={`${styles.triggerText} ${selected ? '' : styles.placeholder}`}>
          {selected ? selected.label : placeholder}
        </span>
        {selected && onClear && !disabled ? (
          <span
            role="button"
            aria-label="清除选择"
            className={styles.clearBtn}
            onClick={e => { e.stopPropagation(); onClear(); }}
          >
            <X size={13} />
          </span>
        ) : (
          <ChevronDown size={14} className={`${styles.chevron} ${open ? styles.chevronUp : ''}`} />
        )}
      </button>
      {open && (
        <div className={styles.panel}>
          <div className={styles.searchBox}>
            <Search size={13} className={styles.searchIcon} />
            <input
              ref={inputRef}
              className={styles.searchInput}
              value={keyword}
              placeholder="搜索素材名称/编码..."
              onChange={e => setKeyword(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
            />
          </div>
          <div className={styles.optionList}>
            {filtered.length === 0 ? (
              <div className={styles.empty}>无匹配素材</div>
            ) : filtered.map(o => (
              <div
                key={o.value}
                className={`${styles.option} ${o.value === value ? styles.optionActive : ''}`}
                onClick={() => { onChange(o.value); setOpen(false); }}
              >
                <span className={styles.optionText}>{o.label}</span>
                {o.value === value && <Check size={13} className={styles.check} />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
